import { event } from 'analytics';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { moveToNextStep } from 'redux/actions/stepActions';
import caseCreationActions from 'redux/actions/caseCreationActions';
import {
  caseCreationSelector,
  caseCreationEditModeSelector,
} from 'redux/selectors/caseCreation';
import { push } from 'connected-react-router';
import routes from 'Routes/routesConfig';
import { useEffect } from 'react';
import { addSuccessAlert } from 'redux/actions/alertActions';
import { useTranslate } from 'hooks/translation-hooks';
import type {
  UseCaseOpeningFormProps,
  FormatDefaultValuesParams,
} from './types';

const { updateCaseCreation, toggleCreateCaseIsEditMode } = caseCreationActions;

const SUCCESS_MESSAGE = 'create_case-edition_success';

const getEventCategory = ({
  isEditMode,
  category,
}: {
  isEditMode?: boolean;
  category: string;
}) => {
  let eventCategory = isEditMode ? `edit_${category}` : category;
  eventCategory =
    eventCategory === 'edit_contract_registration'
      ? 'edit_contract_number'
      : eventCategory;
  return eventCategory;
};

const formatDefaultValues = ({
  defaultValuesFormatter,
  initialValues,
  defaultValues,
}: FormatDefaultValuesParams) => {
  const values = initialValues || defaultValues;
  return defaultValuesFormatter ? defaultValuesFormatter(values) : values;
};

export const useCaseOpeningForm = <D>({
  category,
  initialValues,
  isEmptyLabel,
  submitFormatter,
  defaultValuesFormatter,
  mode = 'onChange',
}: UseCaseOpeningFormProps<D>) => {
  const dispatch = useDispatch();
  const t = useTranslate('caseOpening');
  const defaultValues = useSelector(caseCreationSelector);
  const formDefaultValues = formatDefaultValues({
    defaultValuesFormatter,
    initialValues,
    defaultValues,
  });
  const isEditMode = useSelector(caseCreationEditModeSelector);

  const eventCategory = getEventCategory({ isEditMode, category });

  const {
    clearErrors,
    handleSubmit,
    register,
    reset,
    formState,
    formState: { errors },
    setError,
    setValue,
    getValues,
    watch,
    unregister,
    control,
    trigger,
  } = useForm({
    defaultValues: formDefaultValues,
    mode,
    reValidateMode: 'onChange',
  });
  const { isDirty, isValid } = formState;

  const handleEditMode = () => {
    dispatch(toggleCreateCaseIsEditMode());
    dispatch(addSuccessAlert(t(SUCCESS_MESSAGE)));
    dispatch(push(routes.SUMMARY));
  };

  useEffect(() => {}, [isDirty, isValid]);

  const disabled = !isValid;
  /**
   * This function takes the form's data and handles edit mode as well as next step
   *
   * If you need to save `updateCaseCreation`'s data with other format,
   * you must pass a `submitFormatter` function to `useCaseOpeningForm`
   */
  const onSubmit = async (data: any) => {
    if (formState.isValid) {
      dispatch(
        updateCaseCreation(submitFormatter ? submitFormatter(data) : data),
      );
      let labels;
      if (isEmptyLabel) {
        const fieldValue = getValues(isEmptyLabel);
        const label = fieldValue !== '';
        labels = [label.toString()];
      }
      event('validate', eventCategory, labels);
      if (isEditMode) {
        handleEditMode();
        return;
      }
      dispatch(moveToNextStep());
    }
  };
  return {
    category: eventCategory,
    clearErrors,
    control,
    disabled,
    errors,
    formState,
    getValues,
    handleSubmit,
    onSubmit: handleSubmit(onSubmit),
    register,
    reset,
    setError,
    setValue,
    trigger,
    unregister,
    watch,
  };
};
